<script setup>
import { Head, Link } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Tag from "primevue/tag";
import Dropdown from "primevue/dropdown";
import { ref, watch } from "vue";
import { Inertia } from "@inertiajs/inertia";
import { debounce } from "../../../utils/debounce";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";

const props = defineProps([
    'services',
    'companies',
    'categories',
    'filters',
    'flash',
]);

const confirm = useConfirm();
const toast = useToast();
const search = ref(props.filters.search);
const companyId = ref(props.filters.company_id);

const updateSearch = debounce(() => {
    const params = {
        search: search.value,
    };

    if (Inertia.page.props.auth.user.role === 'super_admin') {
        params.company_id = companyId.value;
    }

    Inertia.get('/management/services', params, { preserveState: true, replace: true });
}, 500);

watch([search, companyId], () => {
    updateSearch();
});

const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF' }).format(value);
};

const confirmDelete = (service) => {
    confirm.require({
        message: `Sind Sie sicher, dass Sie die Dienstleistung "${service.name}" löschen möchten?`,
        header: 'Löschen bestätigen',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        acceptLabel: 'Löschen',
        rejectLabel: 'Abbrechen',
        accept: () => {
            Inertia.delete(`/management/services/${service.uuid}`, {
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    toast.add({
                        severity: 'success',
                        summary: 'Erfolgreich',
                        detail: 'Dienstleistung wurde gelöscht.',
                        life: 5000,
                    });
                }
            });
        }
    });
};

</script>

<template>
    <Layout :flash="flash">
        <Head title="Dienstleistungen" />

        <div class="space-y-6">
            <div class="flex justify-between items-center">
                <h1 class="text-2xl font-bold text-gray-800">Dienstleistungen</h1>
                <Link href="/management/services/create">
                    <Button label="Hinzufügen" icon="pi pi-plus" severity="success" class="!px-4 !py-2" />
                </Link>
            </div>

            <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div class="p-4 border-b border-gray-50 flex gap-4 bg-gray-50/50">
                    <IconField iconPosition="left" class="flex-1 max-w-md">
                        <InputIcon class="pi pi-search"></InputIcon>
                        <InputText v-model="search"
                                   class="w-full"
                                   placeholder="Suche nach Name..." />
                    </IconField>
                    
                    <Dropdown v-if="$page.props.auth.user.role === 'super_admin'"
                              v-model="companyId" 
                              :options="companies" 
                              optionLabel="name" 
                              optionValue="id" 
                              placeholder="Alle Unternehmen" 
                              class="w-64"
                              showClear />
                </div>

                <DataTable :value="services.data" 
                           :rows="20" 
                           dataKey="id"
                           responsiveLayout="scroll"
                           class="p-datatable-sm">
                    <Column field="name" header="Name" sortable class="font-semibold"></Column>
                    <!-- Removed Company Column -->
                    <Column field="category.name" header="Kategorie" sortable></Column>
                    <Column field="duration" header="Dauer" sortable>
                        <template #body="slotProps">
                            {{ slotProps.data.duration }} Min
                        </template>
                    </Column>
                    <Column field="price" header="Preis" sortable>
                        <template #body="slotProps">
                            <span class="font-mono">{{ formatCurrency(slotProps.data.price) }}</span>
                        </template>
                    </Column>
                    <Column field="sort_order" header="Sortierung" sortable></Column>
                    <Column field="is_active" header="Status" sortable>
                        <template #body="slotProps">
                            <Tag :value="slotProps.data.is_active ? 'Aktiv' : 'Inaktiv'" 
                                 :severity="slotProps.data.is_active ? 'success' : 'danger'" />
                        </template>
                    </Column>
                    <Column header="Aktionen" headerStyle="width: 8rem; text-align: center" bodyStyle="text-align: center">
                        <template #body="slotProps">
                            <div class="flex justify-center gap-3">
                                <Link :href="route('management.services.edit', slotProps.data.uuid)">
                                    <Button icon="pi pi-pencil" severity="info" outlined class="!p-2" />
                                </Link>
                                <Button icon="pi pi-trash" severity="danger" outlined class="!p-2" @click.stop="confirmDelete(slotProps.data)" />
                            </div>
                        </template>
                    </Column>

                    <template #empty>
                        <div class="py-12 text-center">
                            <i class="pi pi-list text-4xl text-gray-200 mb-4" />
                            <p class="text-gray-400">Keine Dienstleistungen gefunden.</p>
                        </div>
                    </template>
                </DataTable>

                <div class="p-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center text-sm text-gray-500">
                    <div>
                        Zeige {{ services.from }} bis {{ services.to }} von {{ services.total }} Einträgen
                    </div>
                    <div class="flex gap-2">
                        <Link v-for="link in services.links" 
                              :key="link.label"
                              :href="link.url || '#'"
                              v-html="link.label"
                              :class="[
                                'px-3 py-1 rounded border transition-colors',
                                link.active ? 'bg-black text-[#FFF053] border-black' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
                                !link.url ? 'opacity-50 cursor-not-allowed' : ''
                              ]" />
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<style scoped>
:deep(.p-datatable-thead > tr > th) {
    background: transparent;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 1rem;
}

:deep(.p-datatable-tbody > tr > td) {
    padding: 1rem;
    border-bottom: 1px solid #f8fafc;
}

:deep(.p-datatable-tbody > tr:hover) {
    background-color: #fcfcfd;
}
</style>
